import { z } from 'zod';

// Account security (docs/plans/golive-onboarding-and-account-security.md §3; ADR 0037):
// one-time setup and password reset links, changing your own password, and two-factor
// resets decided by a second administrator.

/** The token from a link: 32 random bytes, base64url (43 characters). */
export const accountLinkTokenSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]{43}$/, 'This link is not complete. Open it again from the start.');

export const accountLinkBodySchema = z.object({ token: accountLinkTokenSchema });

export const ACCOUNT_LINK_PURPOSES = ['setup', 'password_reset'] as const;
export type AccountLinkPurpose = (typeof ACCOUNT_LINK_PURPOSES)[number];

/** What the person opening a link sees before choosing a password. */
export const accountLinkViewSchema = z.object({
  purpose: z.enum(ACCOUNT_LINK_PURPOSES),
  email: z.string(),
  displayName: z.string(),
  expiresAt: z.iso.datetime(),
});

export type AccountLinkView = z.infer<typeof accountLinkViewSchema>;

/** The password rules are checked on the server; the form only checks it isn't empty. */
const newPassword = z.string().min(1, 'Enter a new password.').max(256);

export const redeemAccountLinkBodySchema = z.object({
  token: accountLinkTokenSchema,
  password: newPassword,
});

/** After a link: the same next step as signing in, or `sign_in` when there is nowhere to go. */
export const redeemAccountLinkResultSchema = z.object({
  next: z.enum(['done', 'two_factor', 'two_factor_enrolment', 'sign_in']),
});

export const changePasswordBodySchema = z.object({
  currentPassword: z.string().min(1, 'Enter your current password.').max(256),
  newPassword,
});

/**
 * A link issued for someone. `in_person` is the only delivery today (Wodi sends no email): the
 * link is returned once, for the administrator to hand over. An email delivery would return no
 * URL.
 */
export const issuedAccountLinkSchema = z.object({
  delivery: z.literal('in_person'),
  url: z.string(),
  expiresAt: z.iso.datetime(),
});

export type IssuedAccountLink = z.infer<typeof issuedAccountLinkSchema>;

export const twoFactorResetSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  userDisplayName: z.string(),
  requestedBy: z.object({ id: z.uuid(), displayName: z.string() }),
  requestedAt: z.iso.datetime(),
  reason: z.string(),
  status: z.enum(['requested', 'approved', 'rejected']),
});

export type TwoFactorResetView = z.infer<typeof twoFactorResetSchema>;

export const twoFactorResetListSchema = z.object({ items: z.array(twoFactorResetSchema) });

export const requestTwoFactorResetBodySchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, 'Say why the reset is needed, for example "Lost phone and recovery codes".')
    .max(500),
});

export const decideTwoFactorResetBodySchema = z.object({
  note: z.string().trim().max(500).optional(),
});

/** An administrator's view of someone's sign-in security. Never a secret or a hash. */
export const userSecuritySchema = z.object({
  userId: z.uuid(),
  passwordSet: z.boolean(),
  passwordChangedAt: z.iso.datetime().nullable(),
  lockedUntil: z.iso.datetime().nullable(),
  twoFactor: z.object({ required: z.boolean(), enrolled: z.boolean() }),
  liveLink: z
    .object({
      purpose: z.enum(ACCOUNT_LINK_PURPOSES),
      expiresAt: z.iso.datetime(),
    })
    .nullable(),
  openReset: twoFactorResetSchema.nullable(),
});

export type UserSecurityView = z.infer<typeof userSecuritySchema>;
